import React from 'react';
import Button from '../../../../../components/Atoms/Button.jsx';
import Badge from '../../../../../components/Atoms/Badge.jsx';
import PreLabelSection from './PreLabelSection.jsx';
import useLabelingStore from '../store/useDetectionStore';
import { useI18n } from '../../../../../i18n';

const MODE_OPTIONS = [
    { value: 'myclasses', labelKey: 'aiLabel.mode.myclasses.label', hintKey: 'aiLabel.mode.myclasses.hint' },
    { value: 'smart', labelKey: 'aiLabel.mode.smart.label', hintKey: 'aiLabel.mode.smart.hint' },
    { value: 'coco', labelKey: 'aiLabel.mode.coco.label', hintKey: 'aiLabel.mode.coco.hint' },
];

// Confidence/IoU bounds. Both are real model params (`conf` / `iou` on
// model.predict) — nothing here is invented. 0 or 1 are useless in practice, so
// the control stops just short of both ends.
const CONF_MIN = 0.05;
const CONF_MAX = 0.95;
const IOU_MIN = 0.1;
const IOU_MAX = 0.9;

const clampTo = (value, min, max) => Math.min(max, Math.max(min, value));

const AutoLabelPanel = ({
    autoSettings,
    setAutoSettings,
    setAutoMode,
    isDetecting,
    isStopping,
    batchProgress,
    detectCurrentImage,
    autoLabelAllUnlabeled,
    autoLabelFromMaster,
    setCurrentAsMaster,
    clearMaster,
    masterImageId,
    stopAll,
    preLabel,
}) => {
    const { t } = useI18n();
    const {
        images, currentImageIndex, setCurrentImageIndex, classes,
        acceptAiAnnotations, discardAiAnnotations, getCurrentImage,
    } = useLabelingStore();

    const currentImage = getCurrentImage();
    const aiCount = (currentImage?.annotations || []).filter((a) => a.isAI).length;
    const unlabeledCount = images.filter(
        (img) => (img.annotations?.length ?? 0) === 0 && (img.polygons?.length ?? 0) === 0
    ).length;

    // ── master image summary ──
    const masterIndex = images.findIndex((img) => img.id === masterImageId);
    const masterImage = masterIndex >= 0 ? images[masterIndex] : null;
    const masterBoxes = (masterImage?.annotations || []).filter((a) => !a.isAI);
    const masterClassCount = new Set(
        masterBoxes
            .map((a) => String(a.classId))
            .filter((id) => classes.some((c) => String(c.id) === id))
    ).size;
    const masterTargets = images.filter(
        (img) => img.id !== masterImageId &&
            (img.annotations?.length ?? 0) === 0 && (img.polygons?.length ?? 0) === 0
    ).length;
    const isMasterCurrent = !!masterImage && masterIndex === currentImageIndex;
    const running = isDetecting || !!batchProgress;

    const setConfidence = (raw) => {
        const value = Number(raw);
        if (!Number.isFinite(value)) return;
        setAutoSettings({ confidence: clampTo(value, CONF_MIN, CONF_MAX) });
    };
    const setIou = (raw) => {
        const value = Number(raw);
        if (!Number.isFinite(value)) return;
        setAutoSettings({ iouThreshold: clampTo(value, IOU_MIN, IOU_MAX) });
    };

    return (
        <div className="auto-label-panel">
            <div className="auto-label-panel__header">
                <span className="auto-label-panel__title">
                    <i className="fa fa-robot" aria-hidden="true"></i> {t('aiLabel.panel.title')}
                </span>
                <button
                    type="button"
                    className="auto-label-panel__close"
                    onClick={() => setAutoMode(false)}
                    title={t('aiLabel.panel.close')}
                >
                    <i className="fa fa-times" aria-hidden="true"></i>
                </button>
            </div>

            <div className="auto-label-panel__body">
                {/* Model-in-the-loop: only shows once a version has been trained */}
                <PreLabelSection {...preLabel} disabled={running} />

                <label className="auto-label-panel__label" htmlFor="ai-detect-mode">
                    {t('aiLabel.mode.label')}
                </label>
                <select
                    id="ai-detect-mode"
                    className="form-select form-select-sm auto-label-panel__select"
                    value={autoSettings.detectMode}
                    onChange={(e) => setAutoSettings({ detectMode: e.target.value })}
                    disabled={running || autoSettings.useMaster}
                >
                    {MODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                    ))}
                </select>
                {/*
                  * "My classes only" needs classes. With none, useAutoLabel
                  * silently swaps the mode to Smart — and this hint went on
                  * promising that no new classes would be created, while Smart
                  * invents one per unrecognised detection. The swap itself is a
                  * reasonable bootstrap; doing it invisibly under a hint that
                  * says the opposite is the defect.
                  */}
                <small
                    className={`auto-label-panel__hint${
                        autoSettings.detectMode === 'myclasses' && classes.length === 0
                            ? ' auto-label-panel__hint--warn'
                            : ''
                    }`}
                >
                    {autoSettings.detectMode === 'myclasses' && classes.length === 0
                        ? t('aiLabel.mode.myclasses.noClasses')
                        : t(MODE_OPTIONS.find((o) => o.value === autoSettings.detectMode)?.hintKey)}
                </small>

                {/* ── Confidence: slider + numeric, editable while a run is in flight ── */}
                <label className="auto-label-panel__label mt-2" htmlFor="ai-confidence">
                    {t('aiLabel.confidence.label')}
                </label>
                <div className="auto-label-panel__threshold">
                    <input
                        id="ai-confidence"
                        type="range"
                        className="form-range"
                        min={CONF_MIN}
                        max={CONF_MAX}
                        step="0.01"
                        value={autoSettings.confidence}
                        onChange={(e) => setConfidence(e.target.value)}
                    />
                    <input
                        type="number"
                        className="form-control form-control-sm auto-label-panel__number"
                        min={CONF_MIN}
                        max={CONF_MAX}
                        step="0.01"
                        value={autoSettings.confidence}
                        onChange={(e) => setConfidence(e.target.value)}
                        aria-label={t('aiLabel.confidence.label')}
                    />
                </div>
                <small className="auto-label-panel__hint">{t('aiLabel.confidence.hint')}</small>

                {/* ── IoU / NMS: an existing model param, surfaced not invented ── */}
                <label className="auto-label-panel__label mt-2" htmlFor="ai-iou">
                    {t('aiLabel.iou.label')}
                </label>
                <div className="auto-label-panel__threshold">
                    <input
                        id="ai-iou"
                        type="range"
                        className="form-range"
                        min={IOU_MIN}
                        max={IOU_MAX}
                        step="0.05"
                        value={autoSettings.iouThreshold}
                        onChange={(e) => setIou(e.target.value)}
                        disabled={running}
                    />
                    <input
                        type="number"
                        className="form-control form-control-sm auto-label-panel__number"
                        min={IOU_MIN}
                        max={IOU_MAX}
                        step="0.05"
                        value={autoSettings.iouThreshold}
                        onChange={(e) => setIou(e.target.value)}
                        disabled={running}
                        aria-label={t('aiLabel.iou.label')}
                    />
                </div>
                <small className="auto-label-panel__hint">{t('aiLabel.iou.hint')}</small>

                <div className="form-check form-switch auto-label-panel__switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="auto-on-navigate"
                        checked={autoSettings.autoOnNavigate}
                        onChange={(e) => setAutoSettings({ autoOnNavigate: e.target.checked })}
                        disabled={running}
                    />
                    <label className="form-check-label" htmlFor="auto-on-navigate">
                        {t('aiLabel.nav.autoLabel')}
                    </label>
                </div>

                {/* ── Master-image guided mode ── */}
                <div className="auto-label-panel__master">
                    <div className="form-check form-switch auto-label-panel__switch mb-1">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            id="auto-use-master"
                            checked={autoSettings.useMaster}
                            onChange={(e) => setAutoSettings({ useMaster: e.target.checked })}
                            disabled={running}
                        />
                        <label className="form-check-label" htmlFor="auto-use-master">
                            <i className="fa fa-crosshairs" aria-hidden="true"></i> {t('aiLabel.master.use')}
                        </label>
                    </div>

                    {autoSettings.useMaster && (
                        <>
                            <small className="auto-label-panel__hint">{t('aiLabel.master.hint')}</small>

                            {masterImage ? (
                                <div className="auto-label-panel__master-info">
                                    <div className="auto-label-panel__master-name text-truncate">
                                        {t('aiLabel.master.current', { name: masterImage.fileName })}
                                    </div>
                                    <Badge variant={masterBoxes.length > 0 ? 'success' : 'warning'} icon="fa-vector-square">
                                        {t('aiLabel.master.boxes', {
                                            boxes: masterBoxes.length,
                                            classes: masterClassCount,
                                        })}
                                    </Badge>
                                    {masterBoxes.length === 0 && (
                                        <small className="auto-label-panel__hint">{t('aiLabel.master.noBoxes')}</small>
                                    )}
                                    <div className="d-flex gap-1 mt-1">
                                        {!isMasterCurrent && (
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                className="flex-fill"
                                                onClick={() => setCurrentImageIndex(masterIndex)}
                                                disabled={running}
                                                tooltipText={t('aiLabel.master.goto')}
                                            >
                                                <i className="fa fa-eye" aria-hidden="true"></i> {t('aiLabel.master.goto')}
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="flex-fill"
                                            onClick={clearMaster}
                                            disabled={running}
                                            tooltipText={t('aiLabel.master.clear')}
                                        >
                                            <i className="fa fa-times" aria-hidden="true"></i> {t('aiLabel.master.clear')}
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="w-100 mt-1"
                                    onClick={setCurrentAsMaster}
                                    disabled={running || !currentImage}
                                    tooltipText={t('aiLabel.master.set')}
                                >
                                    <i className="fa fa-thumbtack" aria-hidden="true"></i> {t('aiLabel.master.set')}
                                </Button>
                            )}

                            {masterImage && !isMasterCurrent && (
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="w-100 mt-1"
                                    onClick={setCurrentAsMaster}
                                    disabled={running || !currentImage}
                                    tooltipText={t('aiLabel.master.set')}
                                >
                                    <i className="fa fa-thumbtack" aria-hidden="true"></i> {t('aiLabel.master.set')}
                                </Button>
                            )}

                            {/* Appearance gate: master crops vs. detection crops (CLIP) */}
                            <label className="auto-label-panel__label mt-2" htmlFor="ai-similarity">
                                {t('aiLabel.master.similarity')}
                            </label>
                            <div className="auto-label-panel__threshold">
                                <input
                                    id="ai-similarity"
                                    type="range"
                                    className="form-range"
                                    min="0"
                                    max="0.6"
                                    step="0.01"
                                    value={autoSettings.similarity}
                                    onChange={(e) => setAutoSettings({ similarity: Number(e.target.value) })}
                                    disabled={running}
                                />
                                <input
                                    type="number"
                                    className="form-control form-control-sm auto-label-panel__number"
                                    min="0"
                                    max="0.6"
                                    step="0.01"
                                    value={autoSettings.similarity}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        if (Number.isFinite(value)) {
                                            setAutoSettings({ similarity: clampTo(value, 0, 0.6) });
                                        }
                                    }}
                                    disabled={running}
                                    aria-label={t('aiLabel.master.similarity')}
                                />
                            </div>
                            <small className="auto-label-panel__hint">{t('aiLabel.master.similarityHint')}</small>

                            {!running && (
                                <Button
                                    variant="success"
                                    size="sm"
                                    className="w-100 mt-2"
                                    onClick={autoLabelFromMaster}
                                    disabled={masterBoxes.length === 0 || masterTargets === 0}
                                    tooltipText={t('aiLabel.master.runTip')}
                                >
                                    <i className="fa fa-magic" aria-hidden="true"></i>{' '}
                                    {t('aiLabel.master.run', { count: masterTargets })}
                                </Button>
                            )}
                        </>
                    )}
                </div>

            </div>

            {/* ── Progress / actions / review, PINNED below the scroll area.
                 Stop is available for EVERY run, not just batches — and these
                 are the controls that used to be the ones cut off when the
                 panel outgrew the canvas, which is the worst possible half to
                 lose: the settings are readable again by scrolling, a Detect
                 button that is not on screen simply cannot be pressed. ── */}
            <div className="auto-label-panel__footer">
                {running ? (
                    <div className="auto-label-panel__progress">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <small>
                                {batchProgress
                                    ? t('aiLabel.progress.counts', {
                                        done: batchProgress.done,
                                        total: batchProgress.total,
                                        found: batchProgress.found,
                                    })
                                    : t('aiLabel.action.detecting')}
                            </small>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={stopAll}
                                disabled={isStopping}
                                tooltipText={t('aiLabel.action.stopTip')}
                            >
                                <i className="fa fa-stop" aria-hidden="true"></i>{' '}
                                {isStopping ? t('aiLabel.action.stopping') : t('aiLabel.action.stop')}
                            </Button>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                            <div
                                className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                                style={{
                                    width: batchProgress
                                        ? `${(batchProgress.done / Math.max(1, batchProgress.total)) * 100}%`
                                        : '100%',
                                }}
                            />
                        </div>
                        <small className="auto-label-panel__hint text-truncate d-block">
                            {batchProgress?.currentName || currentImage?.fileName || ''}
                        </small>
                    </div>
                ) : (
                    <div className="auto-label-panel__actions">
                        <Button
                            variant="success"
                            size="sm"
                            className="w-100"
                            onClick={detectCurrentImage}
                            disabled={!currentImage}
                            tooltipText={t('aiLabel.action.detectThisTip')}
                        >
                            <i className="fa fa-magic" aria-hidden="true"></i> {t('aiLabel.action.detectThis')}
                        </Button>
                        <Button
                            variant="outline-success"
                            size="sm"
                            className="w-100 mt-1"
                            onClick={autoLabelAllUnlabeled}
                            disabled={unlabeledCount === 0}
                            tooltipText={t('aiLabel.action.detectAllTip')}
                        >
                            <i className="fa fa-images" aria-hidden="true"></i>{' '}
                            {t('aiLabel.action.detectAll', { count: unlabeledCount })}
                        </Button>
                    </div>
                )}

                {aiCount > 0 && (
                    <div className="auto-label-panel__review">
                        <Badge variant="success" icon="fa-lightbulb">
                            {t('aiLabel.review.suggestions', { count: aiCount })}
                        </Badge>
                        <div className="d-flex gap-1 mt-1">
                            <Button
                                variant="primary"
                                size="sm"
                                className="flex-fill"
                                onClick={() => acceptAiAnnotations(currentImageIndex)}
                                tooltipText={t('aiLabel.review.acceptTip')}
                            >
                                <i className="fa fa-check" aria-hidden="true"></i> {t('aiLabel.review.accept')}
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                className="flex-fill"
                                onClick={() => discardAiAnnotations(currentImageIndex)}
                                tooltipText={t('aiLabel.review.discardTip')}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i> {t('aiLabel.review.discard')}
                            </Button>
                        </div>
                        <small className="auto-label-panel__hint">{t('aiLabel.review.hint')}</small>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AutoLabelPanel;
